import apiClient from "./api";
import { ApiResponse } from "../types";

export interface WrikeAuthConfig {
  authorizationUrl: string;
  redirectUri: string;
}

export interface WrikeTokenResponse {
  token: string;
  refreshToken?: string;
  usuarioId: string;
  email: string;
  nome: string;
  avatarUrl?: string;
  /** Admin ou Visualizador. Ausente na API anterior, que não tinha perfis. */
  perfil?: string;
}

export interface RefreshTokenResponse {
  token: string;
  refreshToken?: string;
}

export const authService = {
  /**
   * Obtém configuração OAuth do Wrike para iniciar o fluxo de login.
   * Em dev local, injeta o parâmetro `state` com a origem local para que
   * o callback de produção redirecione de volta para localhost com o code.
   * O redirect_uri NÃO é alterado (deve ser o registrado no Wrike).
   */
  async getWrikeAuthConfig(): Promise<WrikeAuthConfig> {
    const response = await apiClient.get<
      WrikeAuthConfig | ApiResponse<WrikeAuthConfig>
    >("/auth/wrike/config");

    let config: WrikeAuthConfig;

    // Verificar se a resposta tem o formato ApiResponse ou é direta
    if ("success" in response.data) {
      // Formato ApiResponse
      const apiResponse = response.data as ApiResponse<WrikeAuthConfig>;
      if (!apiResponse.success || !apiResponse.data) {
        throw new Error(
          apiResponse.message || "Erro ao obter configuração OAuth",
        );
      }
      config = apiResponse.data;
    } else {
      // Resposta direta
      config = response.data as WrikeAuthConfig;
    }

    // Em dev local, adiciona state=<origin> para o callback de produção
    // saber que deve redirecionar de volta para o localhost
    const devOrigin = import.meta.env.VITE_DEV_ORIGIN;
    if (devOrigin && config.authorizationUrl) {
      try {
        const url = new URL(config.authorizationUrl);
        url.searchParams.set("state", devOrigin);
        config.authorizationUrl = url.toString();
      } catch {
        console.warn("Não foi possível adicionar state à URL de autorização");
      }
    }

    return config;
  },

  /**
   * Troca o código de autorização por tokens JWT
   */
  async exchangeWrikeCode(code: string): Promise<WrikeTokenResponse> {
    const response = await apiClient.post<
      WrikeTokenResponse | ApiResponse<WrikeTokenResponse> | any
    >("/auth/wrike/token", { code });

    let data: any;

    // Verificar se a resposta tem o formato ApiResponse ou é direta
    if ("success" in response.data) {
      // Formato ApiResponse
      const apiResponse = response.data as ApiResponse<WrikeTokenResponse>;
      if (!apiResponse.success || !apiResponse.data) {
        throw new Error(
          apiResponse.message || "Erro ao processar autenticação",
        );
      }
      data = apiResponse.data;
    } else {
      // Resposta direta
      data = response.data;
    }

    // Normalizar propriedades (API retorna com letra maiúscula)
    const tokenResponse = {
      token: data.Token || data.token,
      refreshToken: data.RefreshToken || data.refreshToken,
      usuarioId: data.UsuarioId || data.usuarioId,
      email: data.Email || data.email,
      nome: data.Nome || data.nome,
      avatarUrl: data.AvatarUrl || data.avatarUrl,
    };

    // Salvar refresh token se presente
    if (tokenResponse.refreshToken) {
      localStorage.setItem("refresh_token", tokenResponse.refreshToken);
    }

    return tokenResponse;
  },

  /**
   * Renova o access token usando refresh token
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<any>("/auth/wrike/refresh", {
      refreshToken,
    });

    let data: any;

    // Verificar se a resposta tem o formato ApiResponse ou é direta
    if ("success" in response.data) {
      // Formato ApiResponse
      if (!response.data.success) {
        throw new Error(response.data.message || "Erro ao renovar token");
      }
      data = response.data.data || {};
    } else {
      // Resposta direta
      data = response.data || {};
    }

    // Normalizar propriedades (API retorna com letra maiúscula, ex: AccessToken)
    const tokenResponse: RefreshTokenResponse = {
      token: data.AccessToken || data.Token || data.token || "",
      refreshToken: data.RefreshToken || data.refreshToken,
    };

    // IMPORTANTE: Salvar tokens ANTES de retornar, garantindo que localStorage é atualizado
    if (tokenResponse.token) {
      localStorage.setItem("auth_token", tokenResponse.token);
    }
    if (tokenResponse.refreshToken) {
      localStorage.setItem("refresh_token", tokenResponse.refreshToken);
    }

    return tokenResponse;
  },

  /**
   * Realiza logout e revoga o JWT
   */
  async logout(): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse<void>>("/auth/logout");

      if (!response.data.success) {
        console.warn("Aviso no logout:", response.data.message);
      }
    } catch (error) {
      // Mesmo em caso de erro, fazer logout local
      console.error("Erro ao fazer logout no servidor:", error);
    }

    // Limpar dados locais
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
  },

  /**
   * Verifica se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem("auth_token");
    return !!token;
  },

  /**
   * Obtém o token JWT armazenado
   */
  getToken(): string | null {
    const token = localStorage.getItem("auth_token");

    return token;
  },

  /**
   * Obtém o refresh token armazenado
   */
  getRefreshToken(): string | null {
    return localStorage.getItem("refresh_token");
  },

  /**
   * Armazena tokens e dados do usuário
   */
  setAuthData(data: WrikeTokenResponse): void {
    localStorage.setItem("auth_token", data.token);

    // Salvar refresh token se presente
    if (data.refreshToken) {
      localStorage.setItem("refresh_token", data.refreshToken);
    }

    localStorage.setItem(
      "user",
      JSON.stringify({
        usuarioId: data.usuarioId,
        email: data.email,
        nome: data.nome,
        avatarUrl: data.avatarUrl,
        perfil: data.perfil,
      }),
    );
  },

  /**
   * Armazena refresh token
   */
  setRefreshToken(refreshToken: string): void {
    localStorage.setItem("refresh_token", refreshToken);
  },
};
